import { useNavigate } from 'react-router-dom';
import { useOverview } from '../api/hooks';
import type { PaymentListItem } from '../api/types';
import { DocRow, DocTable } from '../components/DocRow';
import { I } from '../components/Icons';
import { PayRow, PayTable } from '../components/PayRow';
import { Btn, Empty, H2, Surface } from '../components/ui';
import { cap, czk0, MONTHS, MONTHS_L, todayIso } from '../lib/format';
import { useUi } from '../state/ui';

/** Přehled v režimu „Všechny firmy": souhrn z GET /api/overview přes všechny firmy (widgety jsou per firma). */
export function HomeAll() {
  const navigate = useNavigate();
  const { data: o } = useOverview(undefined);
  const { toast } = useUi();
  const m = todayIso().slice(0, 7);

  const openSum = Number(o?.openTotal ?? 0);
  const overSum = Number(o?.overdueTotal ?? 0);
  const pct = openSum ? Math.round((overSum / openSum) * 100) : 0;
  const months = o?.months ?? [];
  const max = Math.max(...months.map((x) => Number(x.invoiced)), 1);
  const todo = (o?.overdueCount ?? 0) + (o?.unmatchedCount ?? 0);

  return (
    <div className="cols">
      <div>
        <section className="hero">
          <div className="hero-l">Čeká na zaplacení</div>
          <div className="hero-n">{czk0(openSum)}</div>
          <div className="bar">
            <span style={{ width: `${100 - pct}%` }} />
            <span className="bad" style={{ width: `${pct}%` }} />
          </div>
          <div className="hero-row">
            <span>
              <i className="dot ok" />v termínu {czk0(openSum - overSum)}
            </span>
            <span>
              <i className="dot bad" />
              po splatnosti {czk0(overSum)} ({o?.overdueCount ?? 0})
            </span>
          </div>
        </section>
        <div className="quick">
          <Btn variant="primary" onClick={() => navigate('/novy')}>
            {I.plus}Nová faktura
          </Btn>
          <Btn onClick={() => navigate('/platby')}>Platby</Btn>
          <Btn onClick={() => navigate('/prehledy')}>Přehledy</Btn>
        </div>
        <H2>
          {cap(MONTHS_L[Number(m.slice(5)) - 1] ?? '')} {m.slice(0, 4)}
        </H2>
        <Surface className="pad">
          <div className="two">
            <div>
              <div className="k">Vyfakturováno</div>
              <div className="v">{czk0(o?.invoicedMonth ?? 0)}</div>
            </div>
            <div>
              <div className="k">Přijato</div>
              <div className="v ok">{czk0(o?.receivedMonth ?? 0)}</div>
            </div>
          </div>
          <svg
            className="chart"
            viewBox="0 0 300 108"
            role="img"
            aria-label="Vyfakturováno po měsících"
          >
            {months.map((mm, i) => {
              const h = Math.max(2, (Number(mm.invoiced) / max) * 78);
              const x = i * 50 + 10;
              return (
                <g key={mm.month}>
                  <rect
                    x={x}
                    y={86 - h}
                    width="30"
                    height={h}
                    rx="4"
                    fill={i === months.length - 1 ? 'var(--brand)' : '#CFD2CB'}
                  />
                  <text
                    x={x + 15}
                    y="102"
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="600"
                    fill="#8A93A8"
                  >
                    {MONTHS[Number(mm.month.slice(5)) - 1]}
                  </text>
                </g>
              );
            })}
          </svg>
        </Surface>
      </div>
      <div>
        <H2>K vyřízení {todo ? <span className="cnt">{todo}</span> : null}</H2>
        {o?.overdue.length ? (
          <DocTable>
            {o.overdue.map((d) => (
              <DocRow key={d.id} doc={d} />
            ))}
          </DocTable>
        ) : null}
        {o?.unmatched.length ? (
          <PayTable className={o.overdue.length ? 'mt' : ''}>
            {o.unmatched.map((p: PaymentListItem) => (
              <PayRow
                key={p.id}
                payment={p}
                onMatch={() => {
                  navigate('/platby');
                  toast('Spáruj platbu v Platbách');
                }}
              />
            ))}
          </PayTable>
        ) : null}
        {!todo && (
          <Surface>
            <Empty>Vše je vyřízené. Žádné doklady po splatnosti ani nespárované platby.</Empty>
          </Surface>
        )}
        <H2>Poslední doklady</H2>
        <DocTable>
          {o?.recent.length ? (
            o.recent.map((d) => <DocRow key={d.id} doc={d} />)
          ) : (
            <Empty>Zatím žádný doklad. První vystavíš tlačítkem + dole.</Empty>
          )}
        </DocTable>
      </div>
    </div>
  );
}
